/**
 * CustomerManagementPage
 * 
 * Features:
 * - Customer list with search and filters
 * - Multi-event anniversary tracking per customer
 * - Toggle reminders for each event
 * - Upcoming anniversaries view (30 days)
 * - Quick actions: New Order, Contact
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  Users, Search, Phone, Mail, MapPin, Calendar, Bell, BellOff, 
  Plus, ArrowLeft, ChevronRight, Gift, ShoppingCart, Edit2, 
  Trash2, Clock, DollarSign, FileText, MessageSquare, Star, Send, Copy, ExternalLink, CheckCircle,
  ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, ChevronLeft
} from 'lucide-react';
import { API } from '../../utils/constants';

// Occasion options
const OCCASION_OPTIONS = [
  'Wedding', 'Wedding Anniversary', 'Birthday', 'Graduation', 
  'Baby Shower', 'Engagement', 'Corporate Event', 'Holiday Party',
  'Religious Ceremony', 'Retirement', 'Other'
];

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    // Handle date-only strings (YYYY-MM-DD) to avoid timezone shift
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const d = new Date(year, month - 1, day);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    // For ISO dates with time, parse and display in local time
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

// Repeatable occasions that should auto-enable reminders
const REPEATABLE_OCCASIONS = [
  'birthday', 'anniversary', 'wedding anniversary', 'new year', 
  'christmas', 'diwali', 'thanksgiving', 'graduation', 'holi',
  'eid', 'easter', 'memorial day', 'independence day', 'labor day'
];

function isRepeatableOccasion(occasion) {
  if (!occasion) return false;
  const lower = occasion.toLowerCase();
  return REPEATABLE_OCCASIONS.some(ro => lower.includes(ro));
}

function formatCurrency(amount) {
  return `$${Math.floor(amount || 0)}`;
}

function daysUntilAnniversary(dateStr) {
  if (!dateStr) return null;
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(dateStr);
    
    // This year's anniversary
    let anniversary = new Date(today.getFullYear(), eventDate.getMonth(), eventDate.getDate());
    
    // If already passed this year, use next year
    if (anniversary < today) {
      anniversary = new Date(today.getFullYear() + 1, eventDate.getMonth(), eventDate.getDate());
    }
    
    const diffTime = anniversary - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } catch {
    return null;
  }
}

function CustomerManagementPage({ 
  onBack, 
  onCreateOrderForCustomer,
  currentStore 
}) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all, with_reminders, recent
  
  // Sorting state - default to most recent orders first
  const [sortField, setSortField] = useState('last_order_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Selected customer detail view
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Upcoming anniversaries
  const [upcomingAnniversaries, setUpcomingAnniversaries] = useState([]);
  const [showUpcoming, setShowUpcoming] = useState(false);
  
  // Add/Edit event modal
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    date: '',
    occasion: '',
    remind_annually: false,
    notes: ''
  });
  
  // Add/Edit customer modal
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });

  // Feedback request modal
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackOrder, setFeedbackOrder] = useState(null);
  const [feedbackLinkCopied, setFeedbackLinkCopied] = useState(false);

  // Auto-reminder setup modal
  const [showAutoReminderModal, setShowAutoReminderModal] = useState(false);
  const [distinctEventTypes, setDistinctEventTypes] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState({});
  const [loadingEventTypes, setLoadingEventTypes] = useState(false);
  const [applyingReminders, setApplyingReminders] = useState(false);

  const storeId = currentStore?.storeId || currentStore?.id;

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    console.log('🔍 CustomerManagement: fetchCustomers called, storeId:', storeId);
    if (!storeId) {
      console.log('❌ CustomerManagement: No storeId, skipping fetch');
      return;
    }
    setLoading(true);
    try {
      console.log('📡 CustomerManagement: Fetching customers for store:', storeId);
      const resp = await axios.get(`${API}/customers`, {
        params: { store_id: storeId }
      });
      console.log('✅ CustomerManagement: Received', resp.data?.length || 0, 'customers');
      setCustomers(resp.data || []);
    } catch (err) {
      console.error('❌ CustomerManagement: Failed to fetch customers:', err);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  // Fetch upcoming anniversaries
  const fetchUpcoming = useCallback(async () => {
    if (!storeId) return;
    try {
      const resp = await axios.get(`${API}/customers/upcoming-anniversaries`, {
        params: { store_id: storeId, days: 30 }
      });
      setUpcomingAnniversaries(resp.data?.upcoming || []);
    } catch (err) {
      console.error('Failed to fetch upcoming:', err);
    }
  }, [storeId]);

  useEffect(() => {
    fetchCustomers();
    fetchUpcoming();
  }, [fetchCustomers, fetchUpcoming]);

  // Fetch distinct event types for auto-reminder setup
  const fetchDistinctEventTypes = async () => {
    setLoadingEventTypes(true);
    try {
      const resp = await axios.get(`${API}/customers/distinct-event-types`, {
        params: { store_id: storeId }
      });
      const eventTypes = resp.data?.event_types || [];
      setDistinctEventTypes(eventTypes);
      
      // Pre-select repeatable event types
      const preSelected = {};
      eventTypes.forEach(et => {
        preSelected[et.occasion] = et.is_repeatable;
      });
      setSelectedEventTypes(preSelected);
    } catch (err) {
      console.error('Failed to fetch event types:', err);
      toast.error('Failed to load event types');
    } finally {
      setLoadingEventTypes(false);
    }
  };

  // Apply auto-reminders for selected event types
  const applyAutoReminders = async () => {
    const selectedTypes = Object.entries(selectedEventTypes)
      .filter(([_, isSelected]) => isSelected)
      .map(([occasion]) => occasion);
    
    if (selectedTypes.length === 0) {
      toast.error('Please select at least one event type');
      return;
    }
    
    setApplyingReminders(true);
    try {
      const resp = await axios.post(`${API}/customers/auto-create-reminders`, {
        store_id: storeId,
        event_types: selectedTypes
      });
      
      toast.success(`Created ${resp.data.reminders_created} reminders for ${resp.data.customers_updated} customers`);
      setShowAutoReminderModal(false);
      
      // Refresh data
      await fetchCustomers();
      await fetchUpcoming();
      if (selectedCustomer) {
        await fetchCustomerDetail(selectedCustomer.id);
      }
    } catch (err) {
      console.error('Failed to apply auto-reminders:', err);
      toast.error('Failed to create reminders');
    } finally {
      setApplyingReminders(false);
    }
  };

  // Fetch customer detail
  const fetchCustomerDetail = async (customerId) => {
    setLoadingDetail(true);
    try {
      const resp = await axios.get(`${API}/customers/${customerId}`);
      setSelectedCustomer(resp.data?.customer);
      setCustomerOrders(resp.data?.orders || []);
    } catch (err) {
      console.error('Failed to fetch customer detail:', err);
      toast.error('Failed to load customer details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Filter customers
  const filteredCustomers = customers.filter(c => {
    // Search filter
    const matchesSearch = !searchTerm || 
      (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.phone || '').includes(searchTerm) ||
      (c.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Mode filter
    if (filterMode === 'with_reminders') {
      const hasReminder = (c.events || []).some(e => e.remind_annually);
      return matchesSearch && hasReminder;
    }
    if (filterMode === 'recent') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const lastOrder = c.last_order_date ? new Date(c.last_order_date) : null;
      return matchesSearch && lastOrder && lastOrder > thirtyDaysAgo;
    }
    
    return matchesSearch;
  });

  // Sort filtered customers
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortField) {
      case 'name':
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
        break;
      case 'phone':
        aVal = a.phone || '';
        bVal = b.phone || '';
        break;
      case 'total_orders':
        aVal = a.total_orders || 0;
        bVal = b.total_orders || 0;
        break;
      case 'total_spent':
        aVal = a.total_spent || 0;
        bVal = b.total_spent || 0;
        break;
      case 'last_order_date':
        aVal = a.last_order_date ? new Date(a.last_order_date).getTime() : 0;
        bVal = b.last_order_date ? new Date(b.last_order_date).getTime() : 0;
        break;
      case 'events':
        aVal = (a.events || []).length;
        bVal = (b.events || []).length;
        break;
      default:
        aVal = a.name || '';
        bVal = b.name || '';
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get upcoming event for a customer
  const getNextUpcomingEvent = (customer) => {
    const events = (customer.events || []).filter(e => e.remind_annually);
    if (!events.length) return null;
    
    let closest = null;
    let closestDays = Infinity;
    
    for (const event of events) {
      const days = daysUntilAnniversary(event.date);
      if (days !== null && days < closestDays && days <= 30) {
        closest = event;
        closestDays = days;
      }
    }
    
    return closest ? { ...closest, days_until: closestDays } : null;
  };

  // Toggle event reminder
  const handleToggleReminder = async (customerId, eventId) => {
    try {
      const resp = await axios.put(`${API}/customers/${customerId}/events/${eventId}/toggle-reminder`);
      toast.success(resp.data.remind_annually ? 'Reminder enabled' : 'Reminder disabled');
      
      // Refresh data
      if (selectedCustomer?.id === customerId) {
        await fetchCustomerDetail(customerId);
      }
      await fetchCustomers();
      await fetchUpcoming();
    } catch (err) {
      console.error('Failed to toggle reminder:', err);
      toast.error('Failed to update reminder');
    }
  };

  // Add/Edit event
  const handleSaveEvent = async () => {
    if (!eventForm.date || !eventForm.occasion) {
      toast.error('Date and occasion are required');
      return;
    }
    
    try {
      if (editingEvent) {
        await axios.put(
          `${API}/customers/${selectedCustomer.id}/events/${editingEvent.id}`,
          eventForm
        );
        toast.success('Event updated');
      } else {
        await axios.post(
          `${API}/customers/${selectedCustomer.id}/events`,
          eventForm
        );
        toast.success('Event added');
      }
      
      setShowEventModal(false);
      setEditingEvent(null);
      setEventForm({ date: '', occasion: '', remind_annually: false, notes: '' });
      
      // Refresh
      await fetchCustomerDetail(selectedCustomer.id);
      await fetchCustomers();
      await fetchUpcoming();
    } catch (err) {
      console.error('Failed to save event:', err);
      toast.error('Failed to save event');
    }
  };

  // Delete event
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    
    try {
      await axios.delete(`${API}/customers/${selectedCustomer.id}/events/${eventId}`);
      toast.success('Event deleted');
      await fetchCustomerDetail(selectedCustomer.id);
      await fetchCustomers();
      await fetchUpcoming();
    } catch (err) {
      console.error('Failed to delete event:', err);
      toast.error('Failed to delete event');
    }
  };

  // Create reminder from order event
  const handleCreateReminderFromOrder = async (order) => {
    const occasion = order.customer_info?.occasion || 'Event';
    const eventDate = order.customer_info?.start_date || order.customer_info?.event_date;
    
    if (!eventDate) {
      toast.error('No event date found for this order');
      return;
    }
    
    // Check if this event already exists
    const existingEvents = selectedCustomer?.events || [];
    const alreadyExists = existingEvents.some(e => 
      e.date === eventDate && e.occasion.toLowerCase() === occasion.toLowerCase()
    );
    
    if (alreadyExists) {
      toast.info('This event is already tracked');
      return;
    }
    
    // Auto-determine if this should be a repeatable event
    const shouldRepeat = isRepeatableOccasion(occasion);
    
    // Get current year anniversary date (for reminders)
    const originalDate = new Date(eventDate);
    const currentYear = new Date().getFullYear();
    let reminderDate = eventDate;
    
    // If the occasion is repeatable, set reminder for this year (or next if passed)
    if (shouldRepeat) {
      const thisYearDate = new Date(currentYear, originalDate.getMonth(), originalDate.getDate());
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (thisYearDate < today) {
        // Already passed this year, use next year
        reminderDate = `${currentYear + 1}-${String(originalDate.getMonth() + 1).padStart(2, '0')}-${String(originalDate.getDate()).padStart(2, '0')}`;
      } else {
        reminderDate = `${currentYear}-${String(originalDate.getMonth() + 1).padStart(2, '0')}-${String(originalDate.getDate()).padStart(2, '0')}`;
      }
    }
    
    try {
      await axios.post(
        `${API}/customers/${selectedCustomer.id}/events`,
        {
          date: eventDate, // Store original date
          occasion: occasion,
          remind_annually: shouldRepeat, // Auto-enable for repeatable occasions
          notes: `From order on ${formatDate(eventDate)}. Headcount: ${(order.customer_info?.adults || 0) + (order.customer_info?.kids || 0)}`
        }
      );
      
      if (shouldRepeat) {
        toast.success(`Annual reminder added for ${occasion}!`);
      } else {
        toast.success(`Event tracked. Click the bell icon to enable annual reminders.`);
      }
      
      await fetchCustomerDetail(selectedCustomer.id);
      await fetchCustomers();
      await fetchUpcoming();
    } catch (err) {
      console.error('Failed to create reminder:', err);
      toast.error('Failed to create reminder');
    }
  };

  // Check if an order already has a tracked event
  const isOrderEventTracked = (order) => {
    const occasion = order.customer_info?.occasion;
    const eventDate = order.customer_info?.start_date || order.customer_info?.event_date;
    if (!occasion || !eventDate) return false;
    
    const existingEvents = selectedCustomer?.events || [];
    return existingEvents.some(e => 
      e.date === eventDate && e.occasion.toLowerCase() === occasion.toLowerCase()
    );
  };

  // Navigate to previous/next customer in the sorted list
  const getCurrentCustomerIndex = () => {
    if (!selectedCustomer) return -1;
    return sortedCustomers.findIndex(c => c.id === selectedCustomer.id);
  };

  const navigateToPrevCustomer = async () => {
    const currentIndex = getCurrentCustomerIndex();
    if (currentIndex > 0) {
      const prevCustomer = sortedCustomers[currentIndex - 1];
      await fetchCustomerDetail(prevCustomer.id);
    }
  };

  const navigateToNextCustomer = async () => {
    const currentIndex = getCurrentCustomerIndex();
    if (currentIndex < sortedCustomers.length - 1) {
      const nextCustomer = sortedCustomers[currentIndex + 1];
      await fetchCustomerDetail(nextCustomer.id);
    }
  };

  const hasPrevCustomer = () => getCurrentCustomerIndex() > 0;
  const hasNextCustomer = () => getCurrentCustomerIndex() < sortedCustomers.length - 1;

  // Add new customer
  const handleSaveCustomer = async () => {
    if (!customerForm.name || !customerForm.phone) {
      toast.error('Name and phone are required');
      return;
    }
    
    try {
      if (editingCustomer) {
        // Update existing customer using PUT endpoint
        await axios.put(`${API}/customers/${editingCustomer.id}`, {
          name: customerForm.name,
          phone: customerForm.phone,
          email: customerForm.email,
          address: customerForm.address,
          notes: customerForm.notes
        });
        toast.success('Customer updated');
        setShowAddCustomerModal(false);
        setEditingCustomer(null);
        setCustomerForm({ name: '', phone: '', email: '', address: '', notes: '' });
        await fetchCustomers();
        
        // Refresh detail view if same customer
        if (selectedCustomer?.id === editingCustomer.id) {
          await fetchCustomerDetail(editingCustomer.id);
        }
      } else {
        // Create new customer
        const resp = await axios.post(`${API}/customers`, {
          ...customerForm,
          storeId
        });
        toast.success('Customer created');
        setShowAddCustomerModal(false);
        setCustomerForm({ name: '', phone: '', email: '', address: '', notes: '' });
        await fetchCustomers();
        
        // Open the new customer
        if (resp.data?.customer_id) {
          await fetchCustomerDetail(resp.data.customer_id);
        }
      }
    } catch (err) {
      console.error('Failed to save customer:', err);
      toast.error(err.response?.data?.detail || 'Failed to save customer');
    }
  };

  // Open edit customer modal
  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      notes: customer.notes || ''
    });
    setShowAddCustomerModal(true);
  };

  // Handle feedback request
  const handleRequestFeedback = (order) => {
    setFeedbackOrder(order);
    setFeedbackLinkCopied(false);
    setShowFeedbackModal(true);
  };

  // Get feedback link
  const getFeedbackLink = (orderId) => {
    return `${window.location.origin}/feedback/${orderId}`;
  };

  // Copy feedback link
  const copyFeedbackLink = async () => {
    if (!feedbackOrder) return;
    const link = getFeedbackLink(feedbackOrder.id);
    try {
      await navigator.clipboard.writeText(link);
      setFeedbackLinkCopied(true);
      toast.success('Feedback link copied!');
      setTimeout(() => setFeedbackLinkCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  // Mark feedback request as sent
  const markFeedbackSent = async () => {
    if (!feedbackOrder) return;
    try {
      await axios.post(`${API}/orders/${feedbackOrder.id}/request-feedback`);
      toast.success('Marked as sent');
      setShowFeedbackModal(false);
      setFeedbackOrder(null);
      // Refresh customer detail to update order status
      if (selectedCustomer) {
        await fetchCustomerDetail(selectedCustomer.id);
      }
    } catch (err) {
      console.error('Failed to mark feedback request:', err);
      toast.error('Failed to update');
    }
  };

  // Render customer list
  const renderCustomerList = () => (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-orange-600" />
            Customer Management
          </h1>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => {
              setShowAutoReminderModal(true);
              fetchDistinctEventTypes();
            }}
            className="border-orange-300 text-orange-600 hover:bg-orange-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Auto Setup Reminders
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowUpcoming(!showUpcoming)}
            className={showUpcoming ? 'bg-orange-50 border-orange-300' : ''}
          >
            <Gift className="w-4 h-4 mr-2" />
            Upcoming ({upcomingAnniversaries.length})
          </Button>
          <Button onClick={() => setShowAddCustomerModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Upcoming Anniversaries Panel */}
      {showUpcoming && upcomingAnniversaries.length > 0 && (
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4">
          <h3 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
            <Gift className="w-5 h-5" />
            Upcoming Anniversaries (Next 30 Days)
          </h3>
          <div className="grid gap-2 max-h-60 overflow-y-auto">
            {upcomingAnniversaries.map((item, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between bg-white rounded-lg p-3 border border-orange-100"
              >
                <div>
                  <div className="font-medium">{item.customer_name}</div>
                  <div className="text-sm text-gray-600">
                    {item.occasion} · {formatDate(item.anniversary_date)}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={item.days_until <= 7 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}>
                    {item.days_until === 0 ? 'Today!' : `${item.days_until} days`}
                  </Badge>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => fetchCustomerDetail(item.customer_id)}
                      title="View Customer"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    {onCreateOrderForCustomer && (
                      <Button 
                        size="sm"
                        onClick={() => onCreateOrderForCustomer({
                          id: item.customer_id,
                          name: item.customer_name,
                          phone: item.phone,
                          email: item.email
                        })}
                        title="Create Order"
                      >
                        <ShoppingCart className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="customer-search"
          />
        </div>
        <Select value={filterMode} onValueChange={setFilterMode}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Customers</SelectItem>
            <SelectItem value="with_reminders">With Reminders</SelectItem>
            <SelectItem value="recent">Recent (30 days)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Customer List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading customers...</div>
      ) : sortedCustomers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {searchTerm ? 'No customers match your search' : 'No customers yet'}
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th 
                  className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Customer
                    {sortField === 'name' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  </div>
                </th>
                <th 
                  className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('phone')}
                >
                  <div className="flex items-center gap-1">
                    Contact
                    {sortField === 'phone' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  </div>
                </th>
                <th 
                  className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('events')}
                >
                  <div className="flex items-center gap-1">
                    Events
                    {sortField === 'events' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  </div>
                </th>
                <th className="text-center p-3 font-medium">Upcoming</th>
                <th 
                  className="text-right p-3 font-medium cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('total_orders')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Orders / Spent
                    {sortField === 'total_orders' || sortField === 'total_spent' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  </div>
                </th>
                <th className="text-center p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedCustomers.map((customer) => {
                const upcomingEvent = getNextUpcomingEvent(customer);
                const reminderEvents = (customer.events || []).filter(e => e.remind_annually);
                
                return (
                  <tr 
                    key={customer.id}
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => fetchCustomerDetail(customer.id)}
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <td className="p-3">
                      <div className="font-medium">{customer.name || 'Unknown'}</div>
                      {customer.favorite_occasions?.length > 0 && (
                        <div className="text-xs text-gray-500">
                          {customer.favorite_occasions.slice(0, 2).join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1 text-gray-600">
                        <Phone className="w-3 h-3" />
                        {customer.phone || '-'}
                      </div>
                      {customer.email && (
                        <div className="flex items-center gap-1 text-gray-500 text-xs">
                          <Mail className="w-3 h-3" />
                          {customer.email}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {(customer.events || []).slice(0, 3).map((event, idx) => (
                          <Badge 
                            key={idx}
                            variant="outline"
                            className={event.remind_annually ? 'border-orange-300 bg-orange-50' : ''}
                          >
                            {event.remind_annually && <Bell className="w-3 h-3 mr-1" />}
                            {event.occasion}
                          </Badge>
                        ))}
                        {(customer.events || []).length > 3 && (
                          <Badge variant="outline">+{customer.events.length - 3}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      {upcomingEvent ? (
                        <Badge className={upcomingEvent.days_until <= 7 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}>
                          <Gift className="w-3 h-3 mr-1" />
                          {upcomingEvent.days_until}d
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="font-medium">{customer.total_orders || 0} orders</div>
                      <div className="text-xs text-green-600">{formatCurrency(customer.total_spent)}</div>
                    </td>
                    <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                      {onCreateOrderForCustomer && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onCreateOrderForCustomer(customer)}
                          title="Create Order"
                        >
                          <ShoppingCart className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Render customer detail view
  const renderCustomerDetail = () => {
    const currentIndex = getCurrentCustomerIndex();
    const totalCustomers = sortedCustomers.length;
    
    return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between relative">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setSelectedCustomer(null)} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold truncate max-w-[300px]">{selectedCustomer.name || 'Customer'}</h1>
        </div>
        
        {/* Fixed Navigation - Always in center */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1 absolute left-1/2 transform -translate-x-1/2">
          <Button 
            variant="ghost" 
            size="sm"
            className="p-1 h-8 w-8"
            onClick={navigateToPrevCustomer}
            disabled={!hasPrevCustomer()}
            title="Previous customer (←)"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <span className="px-2 min-w-[70px] text-center text-sm font-medium">
            {currentIndex + 1} / {totalCustomers}
          </span>
          <Button 
            variant="ghost" 
            size="sm"
            className="p-1 h-8 w-8"
            onClick={navigateToNextCustomer}
            disabled={!hasNextCustomer()}
            title="Next customer (→)"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => handleEditCustomer(selectedCustomer)}
            data-testid="edit-customer-btn"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Edit
          </Button>
          {selectedCustomer.phone && (
            <Button 
              variant="outline"
              onClick={() => toast.info('SMS integration coming soon! Configure Twilio in Settings.')}
              data-testid="send-sms-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Send SMS
            </Button>
          )}
          {onCreateOrderForCustomer && (
            <Button onClick={() => onCreateOrderForCustomer(selectedCustomer)}>
              <ShoppingCart className="w-4 h-4 mr-2" />
              New Order
            </Button>
          )}
        </div>
      </div>

      {/* Contact Info Card */}
      <div className="bg-white rounded-lg border p-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-gray-400" />
            <div>
              <div className="text-sm text-gray-500">Phone</div>
              <div className="font-medium">{selectedCustomer.phone || '-'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-gray-400" />
            <div>
              <div className="text-sm text-gray-500">Email</div>
              <div className="font-medium">{selectedCustomer.email || '-'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-gray-400" />
            <div>
              <div className="text-sm text-gray-500">Address</div>
              <div className="font-medium">{selectedCustomer.address || '-'}</div>
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-orange-600">{selectedCustomer.total_orders || 0}</div>
            <div className="text-sm text-gray-500">Total Orders</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(selectedCustomer.total_spent)}</div>
            <div className="text-sm text-gray-500">Total Spent</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {selectedCustomer.last_order_date ? formatDate(selectedCustomer.last_order_date) : '-'}
            </div>
            <div className="text-sm text-gray-500">Last Order</div>
          </div>
        </div>
      </div>

      {/* Internal Notes Section */}
      {selectedCustomer.notes && (
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
          <h3 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Internal Notes
          </h3>
          <p className="text-amber-900 whitespace-pre-wrap">{selectedCustomer.notes}</p>
        </div>
      )}

      {/* Events Section */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Gift className="w-5 h-5 text-orange-600" />
            Events & Anniversaries
          </h2>
          <Button 
            size="sm"
            onClick={() => {
              setEditingEvent(null);
              setEventForm({ date: '', occasion: '', remind_annually: false, notes: '' });
              setShowEventModal(true);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Event
          </Button>
        </div>

        {(selectedCustomer.events || []).length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            No events yet. Add an event to track anniversaries.
          </div>
        ) : (
          <div className="space-y-2">
            {selectedCustomer.events.map((event) => {
              const days = daysUntilAnniversary(event.date);
              const isUpcoming = days !== null && days <= 30;
              
              return (
                <div 
                  key={event.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    isUpcoming ? 'bg-orange-50 border-orange-200' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleToggleReminder(selectedCustomer.id, event.id)}
                      className={`p-2 rounded-full ${
                        event.remind_annually 
                          ? 'bg-orange-100 text-orange-600' 
                          : 'bg-gray-200 text-gray-400'
                      }`}
                      title={event.remind_annually ? 'Reminder ON' : 'Reminder OFF'}
                    >
                      {event.remind_annually ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                    </button>
                    <div>
                      <div className="font-medium">{event.occasion}</div>
                      <div className="text-sm text-gray-500">
                        {formatDate(event.date)}
                        {event.notes && <span className="ml-2 italic">· {event.notes}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isUpcoming && (
                      <Badge className={days <= 7 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}>
                        {days === 0 ? 'Today!' : `${days} days`}
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingEvent(event);
                        setEventForm({
                          date: event.date,
                          occasion: event.occasion,
                          remind_annually: event.remind_annually,
                          notes: event.notes || ''
                        });
                        setShowEventModal(true);
                      }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500"
                      onClick={() => handleDeleteEvent(event.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Order History */}
      <div className="bg-white rounded-lg border p-4">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-blue-600" />
          Order History ({customerOrders.length})
        </h2>

        {customerOrders.length === 0 ? (
          <div className="text-center py-6 text-gray-500">No orders yet</div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {customerOrders.map((order) => {
              const hasFeedback = order.feedback?.rating > 0;
              const feedbackRequested = order.feedback?.request_sent;
              const eventDate = order.customer_info?.event_date || order.customer_info?.start_date;
              
              // Calculate days since event with timezone-safe date parsing
              let daysSinceEvent = 0;
              if (eventDate) {
                // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
                let refDate;
                if (typeof eventDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(eventDate)) {
                  const [year, month, day] = eventDate.split('-').map(Number);
                  refDate = new Date(year, month - 1, day);
                } else {
                  refDate = new Date(eventDate);
                }
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                refDate.setHours(0, 0, 0, 0);
                daysSinceEvent = Math.floor((today - refDate) / (1000 * 60 * 60 * 24));
              }
              
              // Allow feedback request if: no feedback yet, event is at least 1 day old, status is confirmed/completed
              const canRequestFeedback = !hasFeedback && daysSinceEvent >= 1 && ['confirmed', 'completed'].includes(order.status);
              const occasion = order.customer_info?.occasion || '';
              const isRepeatable = isRepeatableOccasion(occasion);
              const alreadyTracked = isOrderEventTracked(order);
              
              return (
                <div 
                  key={order.id}
                  className="p-3 rounded-lg bg-gray-50 border"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {occasion || 'Order'}
                        </span>
                        {isRepeatable && !alreadyTracked && (
                          <Badge className="text-xs bg-orange-100 text-orange-700 border-orange-200">
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Repeatable
                          </Badge>
                        )}
                        {alreadyTracked && (
                          <Badge className="text-xs bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Tracked
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(order.customer_info?.start_date || order.created_at)}
                        <span className="mx-2">·</span>
                        {order.items?.length || 0} items
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Add Reminder Button */}
                      {eventDate && !alreadyTracked && (
                        <Button
                          size="sm"
                          variant="outline"
                          className={`text-xs h-7 ${isRepeatable ? 'border-orange-300 text-orange-600 hover:bg-orange-50' : ''}`}
                          onClick={() => handleCreateReminderFromOrder(order)}
                          title={isRepeatable ? 'Add annual reminder' : 'Track this event'}
                        >
                          <Bell className="w-3 h-3 mr-1" />
                          {isRepeatable ? 'Add Reminder' : 'Track Event'}
                        </Button>
                      )}
                      <div className="text-right">
                        <div className="font-medium text-green-600">{formatCurrency(order.total_amount)}</div>
                        <Badge variant="outline" className="text-xs">
                          {order.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  {/* Feedback Section */}
                  <div className="mt-2 pt-2 border-t flex items-center justify-between">
                    {hasFeedback ? (
                      <div className="flex items-center gap-2">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${star <= order.feedback.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        {order.feedback.comments && (
                          <span className="text-sm text-gray-500 truncate max-w-48" title={order.feedback.comments}>
                            &ldquo;{order.feedback.comments}&rdquo;
                          </span>
                        )}
                      </div>
                    ) : canRequestFeedback ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>No feedback yet</span>
                        {feedbackRequested && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600">
                            <Send className="w-3 h-3 mr-1" />
                            Requested
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                    
                    {canRequestFeedback && !feedbackRequested && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => handleRequestFeedback(order)}
                      >
                        <MessageSquare className="w-3 h-3 mr-1" />
                        Get Feedback
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {loadingDetail ? (
        <div className="text-center py-8">Loading...</div>
      ) : selectedCustomer ? (
        renderCustomerDetail()
      ) : (
        renderCustomerList()
      )}

      {/* Add/Edit Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Add Event'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Event Date *</Label>
              <Input
                type="date"
                value={eventForm.date}
                onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Occasion *</Label>
              <Select 
                value={eventForm.occasion} 
                onValueChange={(v) => setEventForm({ ...eventForm, occasion: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select occasion" />
                </SelectTrigger>
                <SelectContent>
                  {OCCASION_OPTIONS.map((occ) => (
                    <SelectItem key={occ} value={occ}>{occ}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="remind"
                checked={eventForm.remind_annually}
                onCheckedChange={(checked) => setEventForm({ ...eventForm, remind_annually: checked })}
              />
              <Label htmlFor="remind" className="cursor-pointer">
                Remind me annually (30 days before)
              </Label>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                placeholder="e.g., 200 guests, outdoor venue"
                value={eventForm.notes}
                onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventModal(false)}>Cancel</Button>
            <Button onClick={handleSaveEvent}>{editingEvent ? 'Update' : 'Add'} Event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Customer Modal */}
      <Dialog open={showAddCustomerModal} onOpenChange={(open) => {
        if (!open) {
          setEditingCustomer(null);
          setCustomerForm({ name: '', phone: '', email: '', address: '', notes: '' });
        }
        setShowAddCustomerModal(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={customerForm.name}
                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                placeholder="Customer name"
              />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input
                value={customerForm.phone}
                onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={customerForm.email}
                onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                placeholder="Email (optional)"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={customerForm.address}
                onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
                placeholder="Address (optional)"
              />
            </div>
            <div>
              <Label>Internal Notes</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={customerForm.notes}
                onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })}
                placeholder="Internal notes about this customer (not visible to customer)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCustomerModal(false)}>Cancel</Button>
            <Button onClick={handleSaveCustomer}>{editingCustomer ? 'Update' : 'Add'} Customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Request Modal */}
      <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Feedback</DialogTitle>
          </DialogHeader>
          
          {feedbackOrder && (
            <div className="space-y-4">
              {/* Order Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between">
                  <div>
                    <p className="font-semibold">{feedbackOrder.customer_info?.name}</p>
                    <p className="text-sm text-gray-500">
                      {feedbackOrder.customer_info?.occasion || 'Order'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">
                      {formatCurrency(feedbackOrder.total_amount)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(feedbackOrder.customer_info?.start_date || feedbackOrder.created_at)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">Contact</p>
                <div className="flex gap-4 text-sm text-gray-600">
                  {selectedCustomer?.phone && (
                    <span>📱 {selectedCustomer.phone}</span>
                  )}
                  {selectedCustomer?.email && (
                    <span>✉️ {selectedCustomer.email}</span>
                  )}
                </div>
              </div>

              {/* Feedback Link */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Feedback Link</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={getFeedbackLink(feedbackOrder.id)}
                    className="flex-1 text-sm bg-gray-100 border rounded-lg px-3 py-2 text-gray-600"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyFeedbackLink}
                  >
                    {feedbackLinkCopied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(getFeedbackLink(feedbackOrder.id), '_blank')}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Send this link to the customer via SMS or email
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowFeedbackModal(false)}>
              Cancel
            </Button>
            <Button onClick={markFeedbackSent} className="bg-orange-500 hover:bg-orange-600">
              <Send className="w-4 h-4 mr-2" />
              Mark as Sent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto Setup Reminders Modal */}
      <Dialog open={showAutoReminderModal} onOpenChange={setShowAutoReminderModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-orange-600" />
              Auto Setup Annual Reminders
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select event types to automatically create annual reminders for all customers with matching orders.
            </p>

            {loadingEventTypes ? (
              <div className="text-center py-8 text-gray-500">Loading event types...</div>
            ) : distinctEventTypes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No event types found in orders</div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-lg p-2">
                {distinctEventTypes.map((et) => (
                  <div 
                    key={et.occasion}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedEventTypes[et.occasion] 
                        ? 'bg-orange-50 border-orange-300' 
                        : 'bg-white hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedEventTypes(prev => ({
                      ...prev,
                      [et.occasion]: !prev[et.occasion]
                    }))}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedEventTypes[et.occasion] || false}
                        onChange={() => {}}
                        className="w-4 h-4 text-orange-600 rounded"
                      />
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {et.occasion}
                          {et.is_repeatable && (
                            <Badge className="text-xs bg-green-100 text-green-700">
                              Repeatable
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {et.customer_count} customers · {et.order_count} orders
                        </div>
                      </div>
                    </div>
                    {et.orders_with_dates > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {et.orders_with_dates} with dates
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>How it works:</strong>
              <ul className="mt-1 ml-4 list-disc text-xs space-y-1">
                <li>Scans all orders with selected event types</li>
                <li>Creates annual reminder events for each customer</li>
                <li>Reminders will appear in &quot;Upcoming Anniversaries&quot;</li>
                <li>Won&apos;t duplicate if reminder already exists</li>
              </ul>
            </div>

            <div className="text-sm text-gray-600">
              <strong>Selected: </strong>
              {Object.entries(selectedEventTypes).filter(([_, v]) => v).length} event types
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAutoReminderModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={applyAutoReminders}
              disabled={applyingReminders || Object.entries(selectedEventTypes).filter(([_, v]) => v).length === 0}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {applyingReminders ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Create Reminders
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CustomerManagementPage;
